import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { workOrdersAPI, assetsAPI, usersAPI, mastersAPI } from '../../api';
import { PageHeader, Card, CardHeader, CardBody, Button, Input, Select, Textarea } from '../ui';
import toast from 'react-hot-toast';
import styles from '../assets/Assets.module.css';

const WO_TABS = [
  { type:'breakdown',  icon:'⚡', label:'Report Breakdown',   color:'#dc2626', bg:'#fff1f2', border:'#fecaca' },
  { type:'pm',         icon:'🔧', label:'Schedule PM',        color:'#1a56db', bg:'#eff6ff', border:'#bfdbfe' },
  { type:'inspection', icon:'🔍', label:'Request Inspection', color:'#15803d', bg:'#f0fdf4', border:'#bbf7d0' },
  { type:'repair',     icon:'✱',  label:'Raise Repair WO',    color:'#b45309', bg:'#fefce8', border:'#fde68a' },
];
const WO_FIELD = {
  breakdown:'is_breakdown', pm:'is_schedule_pm',
  inspection:'is_inspection', repair:'is_repair',
};
const DURATIONS_BD = ['< 2 Hours','2–4 Hours','4–8 Hours','1–3 Days','> 3 Days'];
const DURATIONS_OT = ['< 2 Hours','2–4 Hours','4–8 Hours','Half Day (4 hrs)','1 Day','2–3 Days','1 Week','> 1 Week'];
const PRIORITIES   = ['low','medium','high','critical'];
const fmt = v => (v||'').replace(/_/g,' ').replace(/\b\w/g,c=>c.toUpperCase());

const API = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api').replace(/\/$/, '');
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('iassets_token')}` });

/* ── Asset Typeahead Search ─────────────────────────────────── */
function AssetSearchInput({ assets, value, onChange, style }) {
  const [query,    setQuery]   = useState('');
  const [open,     setOpen]    = useState(false);
  const [focused,  setFocused] = useState(false);
  const [cursor,   setCursor]  = useState(-1);   // keyboard highlight index
  const wrapRef    = useRef(null);
  const listRef    = useRef(null);
  const inputRef   = useRef(null);

  const selected = assets.find(a => String(a.id) === String(value));

  const filtered = open
    ? (query.length === 0
        ? assets.slice(0, 100)
        : assets.filter(a =>
            a.asset_code?.toLowerCase().includes(query.toLowerCase()) ||
            a.name?.toLowerCase().includes(query.toLowerCase())
          ).slice(0, 100)
      )
    : [];

  useEffect(() => {
    const fn = e => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', fn);
    return () => document.removeEventListener('mousedown', fn);
  }, []);

  /* Reset cursor when filtered list changes */
  useEffect(() => { setCursor(-1); }, [filtered.length, query]);

  /* Scroll highlighted item into view */
  useEffect(() => {
    if (cursor < 0 || !listRef.current) return;
    const item = listRef.current.children[cursor];
    if (item) item.scrollIntoView({ block:'nearest' });
  }, [cursor]);

  const select = asset => {
    onChange(String(asset.id));
    setQuery('');
    setOpen(false);
    setCursor(-1);
    setFocused(false);
    inputRef.current?.blur();   // ← move focus to next field
  };

  const clear = () => { onChange(''); setQuery(''); setOpen(false); setCursor(-1); };

  const handleKeyDown = e => {
    if (!open) { if (e.key === 'ArrowDown' || e.key === 'Enter') { setOpen(true); } return; }
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setCursor(c => Math.min(c + 1, filtered.length - 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setCursor(c => Math.max(c - 1, 0));
        break;
      case 'Enter':
        e.preventDefault();
        if (cursor >= 0 && filtered[cursor]) select(filtered[cursor]);
        break;
      case 'Escape':
        setOpen(false);
        setCursor(-1);
        break;
      default: break;
    }
  };

  return (
    <div ref={wrapRef} style={{ position:'relative' }}>
      <div style={{ display:'flex', alignItems:'center', ...style,
        border:'1.5px solid #e2e8f0', borderRadius:8, background:'#fff',
        padding:0, overflow:'hidden',
        borderColor: open ? '#1a56db' : '#e2e8f0' }}>

        <span style={{ padding:'0 10px', color:'#94a3b8', fontSize:14, flexShrink:0 }}>🔍</span>

        <input
          ref={inputRef}
          value={focused ? query : (selected ? `${selected.asset_code} — ${selected.name}` : query)}
          onChange={e => { setQuery(e.target.value); setOpen(true); setCursor(-1); }}
          onFocus={() => { setFocused(true); setQuery(''); setOpen(true); }}
          onBlur={() => setTimeout(() => setFocused(false), 200)}
          onKeyDown={handleKeyDown}
          placeholder={selected ? '' : 'Click to select or type to search…'}
          style={{ flex:1, border:'none', outline:'none', fontSize:13,
            padding:'9px 4px', background:'transparent', color:'#1a1f2e',
            fontWeight: !focused && selected ? 600 : 400 }}
        />

        {value && (
          <button type="button" onClick={clear}
            style={{ padding:'0 10px', background:'none', border:'none',
              cursor:'pointer', color:'#94a3b8', fontSize:16, flexShrink:0, lineHeight:1 }}>✕</button>
        )}
      </div>

      {open && (
        <div ref={listRef} style={{ position:'absolute', top:'100%', left:0, right:0, zIndex:999,
          background:'#fff', border:'1px solid #e2e8f0', borderRadius:8,
          maxHeight:240, overflowY:'auto',
          boxShadow:'0 4px 20px rgba(0,0,0,.12)', marginTop:2 }}>
          {filtered.length === 0 ? (
            <div style={{ padding:'12px 14px', fontSize:12, color:'#94a3b8' }}>
              No assets found for "<strong>{query}</strong>"
            </div>
          ) : filtered.map((a, i) => (
            <div key={a.id}
              onMouseDown={() => select(a)}
              onMouseEnter={() => setCursor(i)}
              style={{ padding:'10px 14px', cursor:'pointer', borderBottom:'1px solid #f8fafc',
                display:'flex', alignItems:'center', gap:10,
                background: cursor === i ? '#eff6ff' : '#fff',
                transition:'background .08s' }}>
              <span style={{ fontSize:11, fontFamily:'monospace', fontWeight:700,
                color: cursor === i ? '#1a56db' : '#1a56db',
                background: cursor === i ? '#dbeafe' : '#eff6ff',
                padding:'2px 7px', borderRadius:5, flexShrink:0 }}>{a.asset_code}</span>
              <span style={{ fontSize:13, color:'#1a1f2e', flex:1,
                overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap',
                fontWeight: cursor === i ? 600 : 400 }}>
                {a.name}
              </span>
              {a.location_name && (
                <span style={{ fontSize:11, color:'#94a3b8', flexShrink:0 }}>📍{a.location_name}</span>
              )}
            </div>
          ))}
          {filtered.length === 100 && (
            <div style={{ padding:'8px 14px', fontSize:11, color:'#94a3b8',
              borderTop:'1px solid #f1f5f9', textAlign:'center' }}>
              Showing first 100 — type to narrow down
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function WorkOrderForm() {
  const { id }    = useParams();
  const navigate  = useNavigate();
  const location  = useLocation();
  const [searchParams] = useSearchParams();

  /* Which tabs to show depends on the source route:
     /maintenance/new → PM only
     /work-orders/new → all except PM                          */
  const isMaintenanceRoute = location.pathname.startsWith('/maintenance');
  const visibleTabs = isMaintenanceRoute
    ? WO_TABS.filter(t => t.type === 'pm')
    : WO_TABS.filter(t => t.type !== 'pm');

  /* Read type from URL — also check window.location as fallback */
  const getTypeFromUrl = () => {
    const fromParams = searchParams.get('type');
    if (fromParams) return fromParams;
    const urlParams  = new URLSearchParams(window.location.search);
    return urlParams.get('type') || 'breakdown';
  };
  const typeFromUrl    = getTypeFromUrl();
  const assetFromUrl   = searchParams.get('asset_id') || '';
  const isEdit         = Boolean(id);

  /* ── Core form state ─────────────────────────────────────── */
  const [woType,         setWoType]         = useState(() => getTypeFromUrl());
  const [title,          setTitle]          = useState('');
  const [assetId,        setAssetId]        = useState(assetFromUrl);
  const [maintTypeId,    setMaintTypeId]    = useState('');
  const [failureTypeId,  setFailureTypeId]  = useState('');
  const [priority,       setPriority]       = useState(typeFromUrl === 'breakdown' ? 'critical' : 'medium');
  const [assignTo,       setAssignTo]       = useState('');
  const [estimatedCost,  setEstimatedCost]  = useState('');
  const [scheduledDate,  setScheduledDate]  = useState('');
  const [dueDate,        setDueDate]        = useState(
    new Date(Date.now() + 7*864e5).toISOString().slice(0,16)
  );
  const [duration,       setDuration]       = useState('');
  const [description,    setDescription]    = useState('');
  const [checklist,      setChecklist]      = useState('');
  const [spareParts,     setSpareParts]     = useState('');
  const [reportedBy,     setReportedBy]     = useState('');
  const [contactNo,      setContactNo]      = useState('');
  const [breakdownId,    setBreakdownId]    = useState('');
  /* PM-specific */
  const [frequencies,    setFrequencies]    = useState([]);
  const [frequencyId,    setFrequencyId]    = useState('');
  const [reminderDays,   setReminderDays]   = useState('3');
  const [nextDueDate,    setNextDueDate]    = useState('');
  const [vendorType,     setVendorType]     = useState('In-House Team');

  /* ── Master data ─────────────────────────────────────────── */
  const [assets,          setAssets]         = useState([]);
  const [users,           setUsers]          = useState([]);
  const [maintenanceTypes,setMtnTypes]       = useState([]);
  const [failureTypes,    setFailureTypes]   = useState([]);
  const [loading,         setLoading]        = useState(false);

  const isBreakdown = woType === 'breakdown';
  const isPM        = woType === 'pm';
  const cfg = WO_TABS.find(t => t.type === woType) || WO_TABS[0];

  /* ── Load reference data ─────────────────────────────────── */
  useEffect(() => {
    assetsAPI.list({ limit: 200 }).then(r => setAssets(r.data.data || [])).catch(()=>{});
    usersAPI.list().then(r => setUsers(r.data.data || [])).catch(()=>{});
    fetch(`${API}/masters/maintenance-types`, { headers: hdrs() })
      .then(r => r.json()).then(d => setMtnTypes(Array.isArray(d) ? d : [])).catch(()=>{});
    fetch(`${API}/masters/failure-types`, { headers: hdrs() })
      .then(r => r.json()).then(d => setFailureTypes(Array.isArray(d) ? d : [])).catch(()=>{});
    fetch(`${API}/masters/frequencies`, { headers: hdrs() })
      .then(r => r.json()).then(d => setFrequencies(Array.isArray(d) ? d : [])).catch(()=>{});
  }, []);

  /* ── Load WO for edit mode ───────────────────────────────── */
  useEffect(() => {
    if (!isEdit) return;
    workOrdersAPI.get(id).then(r => {
      const w = r.data;
      setWoType(w.type || 'repair');
      setTitle(w.title || '');
      setAssetId(String(w.asset_id || ''));
      setMaintTypeId(String(w.maintenance_type_id || ''));
      setFailureTypeId(String(w.failure_type_id || ''));
      setPriority(w.priority || 'medium');
      setAssignTo(String(w.assigned_to || ''));
      setEstimatedCost(w.estimated_cost || '');
      setScheduledDate(w.scheduled_date?.slice(0,10) || '');
      setDueDate(w.due_date?.slice(0,10) || '');
      setDuration(w.estimated_duration || '');
      setDescription(w.description || '');
      setChecklist(w.checklist || '');
      setSpareParts(w.spare_parts_req || '');
      setReportedBy(w.reported_by || '');
      setContactNo(w.contact_number || '');
      setFrequencyId(String(w.frequency_id || ''));
      setReminderDays(String(w.reminder_days || '3'));
      setNextDueDate(w.next_due_date?.slice(0,10) || '');
      setVendorType(w.vendor_type || 'In-House Team');
    }).catch(()=>{});
  }, [id, isEdit]);

  /* Sync woType if URL param changes (e.g. back/forward navigation) */
  useEffect(() => {
    if (isEdit) return;
    const t = getTypeFromUrl();
    setWoType(t);
    setPriority(t === 'breakdown' ? 'critical' : 'medium');
  }, [searchParams]);
  useEffect(() => {
    if (!isBreakdown || isEdit) return;
    fetch(`${API}/masters/wo-numbering-series/next-number`, { headers: hdrs() })
      .then(r => r.ok ? r.json() : null)
      .then(d => setBreakdownId(d?.next_number || ''))
      .catch(() => setBreakdownId(''));
  }, [isBreakdown, isEdit]);

  /* ── Auto-select maintenance type when woType changes ───── */
  useEffect(() => {
    if (!maintenanceTypes.length || isEdit) return;
    const field = WO_FIELD[woType];
    const match = maintenanceTypes.find(m => m.is_active && (m[field]===1||m[field]===true));
    setMaintTypeId(match ? String(match.id) : '');
  }, [woType, maintenanceTypes]);

  /* ── Auto-assign from selected asset + auto-generate title ─ */
  const handleAssetChange = async val => {
    setAssetId(val);
    if (!val) { setTitle(''); return; }

    /* Use list data first for instant title */
    let a = assets.find(a => String(a.id) === String(val));
    if (a) setTitle(`${cfg.label} — ${a.asset_code}`);

    /* Check all possible field names for responsible person */
    const getResponsible = obj =>
      obj?.assigned_to || obj?.responsible_person ||
      obj?.responsible_user_id || obj?.technician_id ||
      obj?.employee_id || obj?.default_assignee || '';

    let responsible = getResponsible(a);

    /* If not in list data, fetch full asset profile */
    if (!responsible && val) {
      try {
        const r = await fetch(`${API}/assets/${val}/profile`, { headers: hdrs() });
        if (r.ok) {
          const full = await r.json();
          responsible = getResponsible(full);
          /* Also update title with full asset code if available */
          if (full.asset_code) setTitle(`${cfg.label} — ${full.asset_code}`);
        }
      } catch { /* silent — fallback to manual selection */ }
    }

    if (responsible) setAssignTo(String(responsible));
  };

  /* Regenerate title when WO type tab changes (asset already selected) */
  useEffect(() => {
    if (!assetId || isEdit) return;
    const a = assets.find(a => String(a.id) === String(assetId));
    if (a) setTitle(`${cfg.label} — ${a.asset_code}`);
  }, [woType, cfg.label]);

  /* ── Filtered maintenance types for current WO type ─────── */
  const filteredMtn = maintenanceTypes.filter(m => {
    if (!m.is_active) return false;
    const f = WO_FIELD[woType];
    return !f || m[f] === 1 || m[f] === true;
  });

  /* ── Submit ──────────────────────────────────────────────── */
  const submit = async e => {
    e.preventDefault();
    if (!assetId)            { toast.error('Asset is required'); return; }
    if (!description.trim()) { toast.error('Description is required'); return; }
    setLoading(true);
    try {
      const a    = assets.find(a => String(a.id) === String(assetId));
      const body = {
        title:               title.trim() || `${cfg.label} — ${a?.asset_code || ''}`,
        asset_id:            parseInt(assetId),
        type:                woType,
        maintenance_type_id: maintTypeId ? parseInt(maintTypeId) : null,
        priority,
        assigned_to:         assignTo ? parseInt(assignTo) : null,
        estimated_cost:      parseFloat(estimatedCost) || 0,
        scheduled_date:      scheduledDate || null,
        due_date:            dueDate ? dueDate.slice(0,10) : null,
        estimated_duration:  duration || null,
        description,
        checklist:           checklist    || null,
        spare_parts_req:     spareParts   || null,
        ...(isBreakdown && {
          failure_type_id: failureTypeId ? parseInt(failureTypeId) : null,
          reported_by:     reportedBy || null,
          contact_number:  contactNo  || null,
        }),
        ...(isPM && {
          frequency_id:   frequencyId  ? parseInt(frequencyId)  : null,
          reminder_days:  reminderDays ? parseInt(reminderDays) : null,
          next_due_date:  nextDueDate  || null,
          vendor_type:    vendorType   || null,
        }),
      };
      if (isEdit) {
        await workOrdersAPI.update(id, body);
        toast.success('Work order updated');
        navigate(`/work-orders/${id}`);
      } else {
        const r = await workOrdersAPI.create(body);
        toast.success(`${r.data.message || 'Work Order'} created`);
        navigate('/work-orders');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || err.response?.data?.error || 'Failed');
    } finally {
      setLoading(false);
    }
  };

  /* ── Styles ──────────────────────────────────────────────── */
  const IS = { width:'100%', border:'1.5px solid #e2e8f0', borderRadius:8,
    padding:'8px 11px', fontSize:13, outline:'none', boxSizing:'border-box', background:'#fff' };
  const LS = { fontSize:12, fontWeight:600, color:'#475569', display:'block',
    marginBottom:5, textTransform:'uppercase', letterSpacing:'.04em' };

  return (
    <div style={{ maxWidth: 860 }}>
      <PageHeader
        title={isEdit ? 'Edit Work Order' : cfg.label}
        subtitle={isEdit ? 'Update work order details' : `Create a new ${cfg.label.toLowerCase()} work order`}
        actions={<Button onClick={() => navigate('/work-orders')}>← Back</Button>}
      />

      <Card>
        {/* ── WO Type Tabs (create mode only, hidden when single tab) ── */}
        {!isEdit && visibleTabs.length > 1 && (
          <div style={{ display:'flex', gap:6, padding:'14px 20px 0',
            borderBottom:'2px solid #f1f5f9', flexWrap:'wrap' }}>
            {visibleTabs.map(t => (
              <button key={t.type} type="button"
                onClick={() => {
                  const newCfg = t;
                  setWoType(t.type);
                  setPriority(t.type === 'breakdown' ? 'critical' : 'medium');
                  /* Regenerate title if asset already selected */
                  if (assetId) {
                    const a = assets.find(a => String(a.id) === String(assetId));
                    if (a) setTitle(`${newCfg.label} — ${a.asset_code}`);
                  }
                }}
                style={{ padding:'8px 16px',
                  border:`1.5px solid ${woType===t.type ? t.color : '#e2e8f0'}`,
                  borderBottom: woType===t.type ? 'none' : '1.5px solid #e2e8f0',
                  borderRadius:'8px 8px 0 0', fontSize:12, fontWeight:700,
                  cursor:'pointer', background: woType===t.type ? t.bg : '#fff',
                  color: woType===t.type ? t.color : '#64748b',
                  marginBottom: woType===t.type ? '-2px' : '0',
                  transition:'all .12s' }}>
                {t.icon} {t.label}
              </button>
            ))}
          </div>
        )}

        <CardBody>
          <form onSubmit={submit}>

            {/* ── Info Banner ── */}
            {isBreakdown && (
              <div style={{ background:'#fff5f5', border:'1px solid #fecaca',
                borderRadius:8, padding:'10px 14px', marginBottom:16,
                display:'flex', gap:10 }}>
                <span>⚡</span>
                <div>
                  <div style={{ fontSize:12, fontWeight:700, color:'#dc2626' }}>Emergency Breakdown Report</div>
                  <div style={{ fontSize:12, color:'#dc2626', marginTop:2, lineHeight:1.5 }}>
                    This will immediately create a critical WO and notify the maintenance team and plant manager.
                  </div>
                </div>
              </div>
            )}
            {isPM && (
              <div style={{ background:'#eff6ff', border:'1px solid #bfdbfe',
                borderRadius:8, padding:'10px 14px', marginBottom:16,
                display:'flex', gap:10 }}>
                <span>ℹ</span>
                <div>
                  <div style={{ fontSize:12, fontWeight:700, color:'#1a56db' }}>Preventive Maintenance Request</div>
                  <div style={{ fontSize:12, color:'#1a56db', marginTop:2, lineHeight:1.5 }}>
                    This will schedule a PM work order and notify the assigned technician on the due date.
                  </div>
                </div>
              </div>
            )}

            {/* Breakdown ID */}
            {isBreakdown && !isEdit && (
              <div style={{ display:'flex', alignItems:'center',
                justifyContent:'space-between', background:'#eff6ff',
                border:'1px solid #bfdbfe', borderRadius:8,
                padding:'10px 16px', marginBottom:16 }}>
                <div>
                  <div style={{ fontSize:11, fontWeight:700, color:'#1a56db',
                    textTransform:'uppercase', letterSpacing:'.06em' }}>Breakdown ID</div>
                  <div style={{ fontSize:11, color:'#94a3b8', marginTop:2 }}>
                    Auto-assigned from WO Numbering Series
                  </div>
                </div>
                <div style={{ fontSize:20, fontWeight:800, color:'#1a56db', fontFamily:'monospace' }}>
                  {breakdownId
                    ? breakdownId
                    : <span style={{ fontSize:12, color:'#f59e0b' }}>⚠ Not configured</span>}
                </div>
              </div>
            )}

            {/* Title — auto-generated from WO type + Asset */}
            <div style={{ marginBottom:16 }}>
              <label style={{ ...LS, display:'flex', alignItems:'center', gap:8 }}>
                Title
                <span style={{ fontSize:10, color:'#16a34a', background:'#f0fdf4',
                  padding:'1px 7px', borderRadius:4, fontWeight:600 }}>
                  Auto-generated
                </span>
              </label>
              <input value={title}
                readOnly disabled
                placeholder={assetId ? '' : `Select an asset to auto-generate title…`}
                style={{ ...IS, background:'#f8fafc', color: title ? '#1a1f2e' : '#94a3b8',
                  cursor:'not-allowed', fontWeight: title ? 600 : 400 }}/>
              {!assetId && (
                <div style={{ fontSize:11, color:'#94a3b8', marginTop:3 }}>
                  Title will be set automatically once you select an asset.
                </div>
              )}
            </div>

            {/* Asset */}
            <div style={{ marginBottom:16 }}>
              <label style={LS}>Asset *</label>
              <AssetSearchInput
                assets={assets}
                value={assetId}
                onChange={val => handleAssetChange(val)}
                style={{ width:'100%', boxSizing:'border-box' }}
              />
            </div>

            {/* Ticket Type / Maintenance Type */}
            {filteredMtn.length > 0 && (
              <div style={{ marginBottom:16 }}>
                <label style={LS}>Ticket Type / Maintenance Type</label>
                <select value={maintTypeId} onChange={e => setMaintTypeId(e.target.value)} style={IS}>
                  <option value="">— Select Maintenance Type —</option>
                  {filteredMtn.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              </div>
            )}

            {/* Breakdown Details section */}
            {isBreakdown && (
              <div style={{ background:'#fff8f8', border:'1px solid #fecaca',
                borderRadius:10, padding:'14px 16px', marginBottom:16,
                display:'flex', flexDirection:'column', gap:12 }}>
                <div style={{ fontSize:11, fontWeight:700, color:'#dc2626',
                  textTransform:'uppercase', letterSpacing:'.06em' }}>
                  ⚡ Breakdown Details
                </div>

                {/* Failure Type */}
                {failureTypes.length > 0 && (
                  <div>
                    <label style={LS}>Failure Type</label>
                    <select value={failureTypeId}
                      onChange={e => setFailureTypeId(e.target.value)} style={IS}>
                      <option value="">— Select Failure Type —</option>
                      {failureTypes.filter(f => f.is_active !== false)
                        .map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                    </select>
                  </div>
                )}

                {/* Reported By + Contact Number */}
                <div className={`${styles.form_grid} ${styles.cols_2}`}>
                  <div>
                    <label style={LS}>Reported By</label>
                    <input value={reportedBy} onChange={e => setReportedBy(e.target.value)}
                      placeholder="Name of reporter" style={IS}/>
                  </div>
                  <div>
                    <label style={LS}>Contact Number</label>
                    <input value={contactNo} onChange={e => setContactNo(e.target.value)}
                      placeholder="+91 98765 43210" style={IS}/>
                  </div>
                </div>
              </div>
            )}

            {/* ── PM Configuration section ── */}
            {isPM && (
              <div style={{ background:'#f0f6ff', border:'1px solid #bfdbfe',
                borderRadius:10, padding:'14px 16px', marginBottom:16,
                display:'flex', flexDirection:'column', gap:14 }}>
                <div style={{ fontSize:11, fontWeight:700, color:'#1a56db',
                  textTransform:'uppercase', letterSpacing:'.06em' }}>
                  🔧 PM Configuration
                </div>
                <div className={`${styles.form_grid} ${styles.cols_2}`}>
                  <div>
                    <label style={LS}>Frequency</label>
                    <select value={frequencyId} onChange={e => setFrequencyId(e.target.value)} style={IS}>
                      <option value="">— Select Frequency —</option>
                      {frequencies.length > 0
                        ? frequencies.filter(f => f.is_active !== false)
                            .map(f => <option key={f.id} value={f.id}>{f.name || f.frequency_name}</option>)
                        : ['Daily','Weekly','Monthly','Quarterly','Half-Yearly','Yearly']
                            .map(f => <option key={f} value={f.toLowerCase().replace('-','_')}>{f}</option>)
                      }
                    </select>
                  </div>
                  <div>
                    <label style={LS}>Reminder (days before)</label>
                    <input type="number" value={reminderDays} min="0" max="90"
                      onChange={e => setReminderDays(e.target.value)}
                      placeholder="3" style={IS}/>
                  </div>
                </div>
                <div className={`${styles.form_grid} ${styles.cols_2}`}>
                  <div>
                    <label style={LS}>Next Due Date</label>
                    <input type="date" value={nextDueDate}
                      onChange={e => setNextDueDate(e.target.value)} style={IS}/>
                  </div>
                  <div>
                    <label style={LS}>Vendor / Service Partner</label>
                    <select value={vendorType} onChange={e => setVendorType(e.target.value)} style={IS}>
                      {['In-House Team','External Vendor','OEM Partner','Contractor'].map(v =>
                        <option key={v} value={v}>{v}</option>
                      )}
                    </select>
                  </div>
                </div>
              </div>
            )}

            {/* Priority + Due Date */}
            <div className={`${styles.form_grid} ${styles.cols_2}`} style={{ marginBottom:16 }}>
              <div>
                <label style={LS}>Priority *</label>
                <select value={priority} onChange={e => setPriority(e.target.value)}
                  style={IS} required>
                  {PRIORITIES.map(p => <option key={p} value={p}>{fmt(p)}</option>)}
                </select>
              </div>
              <div>
                <label style={LS}>Due Date / Needed By</label>
                <input type="datetime-local" value={dueDate}
                  onChange={e => setDueDate(e.target.value)} style={IS}/>
              </div>
            </div>

            {/* Assign To + Duration */}
            <div className={`${styles.form_grid} ${styles.cols_2}`} style={{ marginBottom:16 }}>
              <div>
                <label style={{ ...LS, display:'flex', alignItems:'center', gap:6 }}>
                  Assign To
                  {assignTo && (
                    <span style={{ fontSize:10, color:'#16a34a', background:'#f0fdf4',
                      padding:'1px 7px', borderRadius:4, fontWeight:600 }}>
                      ✓ From Asset
                    </span>
                  )}
                </label>
                <select value={assignTo} onChange={e => setAssignTo(e.target.value)} style={IS}>
                  <option value="">— Auto-assign —</option>
                  {users.map(u => <option key={u.id} value={String(u.id)}>{u.name}</option>)}
                </select>
              </div>
              <div>
                <label style={LS}>Estimated Duration</label>
                <select value={duration} onChange={e => setDuration(e.target.value)} style={IS}>
                  <option value="">— Select Duration —</option>
                  {(isBreakdown ? DURATIONS_BD : DURATIONS_OT).map(d =>
                    <option key={d} value={d}>{d}</option>
                  )}
                </select>
              </div>
            </div>

            {/* Estimated Cost + Scheduled Date */}
            <div className={`${styles.form_grid} ${styles.cols_2}`} style={{ marginBottom:16 }}>
              <div>
                <label style={LS}>Estimated Cost (₹)</label>
                <input type="number" value={estimatedCost}
                  onChange={e => setEstimatedCost(e.target.value)}
                  placeholder="0" min="0" style={IS}/>
              </div>
              <div>
                <label style={LS}>Scheduled Date</label>
                <input type="date" value={scheduledDate}
                  onChange={e => setScheduledDate(e.target.value)} style={IS}/>
              </div>
            </div>

            {/* Description */}
            <div style={{ marginBottom:16 }}>
              <label style={LS}>Description / Problem Statement *</label>
              <textarea value={description} onChange={e => setDescription(e.target.value)}
                placeholder={isBreakdown
                  ? 'Describe the breakdown — what happened, when, unusual sounds/smells…'
                  : 'Detailed description of work required…'}
                rows={4} style={{ ...IS, resize:'vertical', fontFamily:'inherit' }}/>
            </div>

            {/* Checklist / Instructions */}
            <div style={{ marginBottom:16 }}>
              <label style={LS}>
                Checklist / Instructions
                <span style={{ fontSize:10, color:'#94a3b8', marginLeft:6,
                  fontWeight:400, textTransform:'none' }}>
                  (one item per line)
                </span>
              </label>
              <textarea value={checklist} onChange={e => setChecklist(e.target.value)}
                placeholder={'e.g.\n1. Check oil level\n2. Inspect belts\n3. Test run for 5 mins'}
                rows={4} style={{ ...IS, resize:'vertical', fontFamily:'inherit' }}/>
            </div>

            {/* Spare Parts Required */}
            <div style={{ marginBottom:20 }}>
              <label style={LS}>
                Spare Parts Required
                <span style={{ fontSize:10, color:'#94a3b8', marginLeft:6,
                  fontWeight:400, textTransform:'none' }}>
                  (one item per line)
                </span>
              </label>
              <textarea value={spareParts} onChange={e => setSpareParts(e.target.value)}
                placeholder={'e.g.\n- Oil filter (x1)\n- V-belt type A (x2)\n- Bearing 6205 (x1)'}
                rows={4} style={{ ...IS, resize:'vertical', fontFamily:'inherit' }}/>
            </div>

            {/* Actions */}
            <div className={styles.form_actions}>
              <Button variant="primary" type="submit" loading={loading}
                style={{ background: loading ? '#93c5fd' : cfg.color, border:'none' }}>
                {loading ? 'Saving…' : (isEdit ? '✓ Update Work Order' : `${cfg.icon} Create Work Order`)}
              </Button>
              <Button type="button" onClick={() => navigate('/work-orders')}>Cancel</Button>
            </div>

          </form>
        </CardBody>
      </Card>
    </div>
  );
}

